<template>
	<panel :show="show" class="verify-caller-id-section" large @close="close">
		<template #title>
			Verify Caller ID
		</template>
		<template>
			<!-- Step 1: Setup form -->
			<form
				v-if="step === STEPS.SETUP"
				class="verify-caller-id-section__form"
				@submit.prevent="onPlaceVerificationCall"
			>
				<p class="verify-caller-id-section__intro">
					We'll call your office number to confirm Clarus has
					authority to mask outbound calls as that number.
				</p>

				<div class="verify-caller-id-section__field">
					<label
						class="verify-caller-id-section__label"
						for="verify-caller-id-number"
					>
						We'll call this office number
					</label>
					<input
						id="verify-caller-id-number"
						v-model="rawPanelPhoneNumber"
						type="text"
						class="verify-caller-id-section__input-readonly"
						placeholder="Phone number"
						readonly
					/>
				</div>

				<div class="verify-caller-id-section__field">
					<label
						class="verify-caller-id-section__label"
						for="verify-caller-id-extension"
					>
						Extension Code
					</label>
					<input
						id="verify-caller-id-extension"
						v-model="extensionCode"
						type="text"
						class="verify-caller-id-section__input"
						placeholder="Extension code"
					/>
					<p v-if="extensionCodeErrorMessage">
						<span class="verify-caller-id-section__field-error">
							{{ extensionCodeErrorMessage }}
						</span>
					</p>
					<p class="verify-caller-id-section__field-help">
						Enter the IVR navigation sequence. Use 'w' for a
						1-second pause and digits 0–9, *, and #.
						<br />Example: w2w3 waits, presses 2, waits, presses 3.
					</p>
				</div>

				<div class="verify-caller-id-section__callout">
					<p class="verify-caller-id-section__callout-title">
						Important — someone must be at this phone
					</p>
					<p class="verify-caller-id-section__callout-body">
						You'll be asked to enter a 6-digit code. We'll show the
						code on the next screen. Customer Success can be on a
						parallel call to help, but the code must be entered at
						the office phone.
					</p>
				</div>

				<div class="verify-caller-id-section__actions">
					<button
						type="button"
						class="verify-caller-id-section__btn verify-caller-id-section__btn--secondary"
						@click="close"
					>
						Cancel
					</button>
					<button
						type="submit"
						class="verify-caller-id-section__btn verify-caller-id-section__btn--primary"
						:disabled="
							isPlacingCall ||
								!canPlaceVerificationCall ||
								Boolean(extensionCodeErrorMessage)
						"
					>
						{{
							isPlacingCall
								? 'Placing call...'
								: 'Place Verification Call'
						}}
					</button>
				</div>
			</form>

			<!-- Step 2+: In-call / timeout / success -->
			<div v-else class="verify-caller-id-section__call-view">
				<div
					class="verify-caller-id-section__status-banner"
					:class="statusBannerClass"
					role="status"
					aria-live="polite"
				>
					{{ statusBannerText }}
				</div>

				<div
					v-if="step !== STEPS.SUCCESS"
					class="verify-caller-id-section__timer-row"
				>
					<span class="verify-caller-id-section__timer-label">
						Time elapsed
					</span>
					<span
						class="verify-caller-id-section__timer-value"
						:class="{
							'verify-caller-id-section__timer-value--danger':
								step === STEPS.TIMEOUT,
						}"
					>
						{{ formattedElapsedTime }}
					</span>
				</div>

				<div
					v-if="step !== STEPS.SUCCESS"
					class="verify-caller-id-section__pin-box"
					:class="{
						'verify-caller-id-section__pin-box--timeout':
							step === STEPS.TIMEOUT,
					}"
				>
					<p class="verify-caller-id-section__pin-label">
						PIN — READ ALOUD WHEN ANSWERED
					</p>
					<div
						class="verify-caller-id-section__pin-digits"
						aria-label="Verification PIN"
					>
						<span
							v-for="(digit, index) in pinDigits"
							:key="index"
							class="verify-caller-id-section__pin-digit"
						>
							{{ digit }}
						</span>
					</div>
				</div>

				<div
					v-if="step === STEPS.SUCCESS"
					class="verify-caller-id-section__success-box"
				>
					<div class="verify-caller-id-section__success-text">
						Your office number
						<div>
							<strong
								class="verify-caller-id-section__success-number"
							>
								{{ formattedPanelPhoneNumber }}
							</strong>
						</div>
						is now verified. Outbound calls from Clarus will display
						this number.
					</div>
				</div>

				<p
					v-if="step === STEPS.CALLING || step === STEPS.TIMEOUT"
					class="verify-caller-id-section__instruction"
				>
					Ask the person who answers to enter this PIN on their keypad
					to complete verification.
				</p>

				<div
					v-if="step === STEPS.TIMEOUT"
					class="verify-caller-id-section__connection-error"
					role="alert"
				>
					Unable to connect -hit cancel and try again.
				</div>

				<div
					class="verify-caller-id-section__actions verify-caller-id-section__actions--center"
				>
					<button
						type="button"
						class="verify-caller-id-section__btn verify-caller-id-section__btn--secondary"
						@click="close"
					>
						{{ step === STEPS.TIMEOUT ? 'Close' : 'Cancel' }}
					</button>
				</div>
			</div>
		</template>
	</panel>
</template>

<script>
import Panel from './Panel'
import PanelMixin from '@/mixins/PanelMixin'
import util from '@/util'
import { mapActions, mapGetters } from 'vuex'

const STEPS = {
	SETUP: 'setup',
	CALLING: 'calling',
	TIMEOUT: 'timeout',
	SUCCESS: 'success',
}

/** Seconds before the verification call is treated as timed out. */
const VERIFICATION_TIMEOUT_SECONDS = 120

export default {
	components: {
		Panel,
	},

	computed: {
		...mapGetters({
			partner: 'partners/active',
		}),

		canPlaceVerificationCall() {
			return (
				Boolean(this.panelPhoneNumber) && !this.panelPhoneErrorMessage
			)
		},

		formattedElapsedTime() {
			const minutes = Math.floor(this.elapsedSeconds / 60)
			const seconds = this.elapsedSeconds % 60
			return `${minutes}:${String(seconds).padStart(2, '0')}`
		},

		formattedPanelPhoneNumber() {
			return util.newFormatPhoneNumberOMD(this.panelPhoneNumber)
		},

		extensionCodeErrorMessage() {
			const code = this.extensionCode

			if (!code) {
				return ''
			}

			const isValid = /^[0-9w*#]+$/.test(code)

			if (!isValid) {
				return 'Invalid extension code. Please enter the correct code and place the call.'
			}

			return ''
		},

		pinDigits() {
			return String(this.verificationPin || '')
				.split('')
				.filter(Boolean)
		},

		rawPanelPhoneNumber: {
			get() {
				return util.newFormatPhoneNumberOMD(this.panelPhoneNumber)
			},
			set(value) {
				this.panelPhoneNumber = util.removeNonNumericCharsOMD(value)
			},
		},

		statusBannerClass() {
			return {
				'verify-caller-id-section__status-banner--connecting':
					this.step === STEPS.CALLING,
				'verify-caller-id-section__status-banner--timeout':
					this.step === STEPS.TIMEOUT,
				'verify-caller-id-section__status-banner--success':
					this.step === STEPS.SUCCESS,
			}
		},

		statusBannerText() {
			if (this.step === STEPS.CALLING) {
				return 'Connecting to office phone...'
			}

			if (this.step === STEPS.TIMEOUT) {
				return 'Call timed out'
			}

			if (this.step === STEPS.SUCCESS) {
				return 'Caller ID Verified'
			}

			return ''
		},
	},

	methods: {
		close() {
			this.stopElapsedTimer()
			this.stopVerificationStatusPolling()
			this.$emit('close')
		},

		resetForm() {
			this.stopElapsedTimer()
			this.stopVerificationStatusPolling()
			this.step = STEPS.SETUP
			this.panelPhoneNumber = this.getSanitizedPhoneNumber(
				this.phoneNumber
			)
			this.extensionCode = ''
			this.panelPhoneErrorMessage = ''
			this.verificationPin = ''
			this.elapsedSeconds = 0
			this.isPlacingCall = false
		},

		getSanitizedPhoneNumber(value) {
			if (!value) {
				return ''
			}

			return String(value)
				.replace(/^\+1/, '')
				.replace(/\D/g, '')
		},

		startElapsedTimer() {
			this.stopElapsedTimer()
			this.elapsedSeconds = 0

			this.elapsedTimerId = window.setInterval(() => {
				this.elapsedSeconds += 1

				if (
					this.step === STEPS.CALLING &&
					this.elapsedSeconds >= VERIFICATION_TIMEOUT_SECONDS
				) {
					this.onVerificationTimeout()
				}
			}, 1000)
		},

		stopElapsedTimer() {
			if (this.elapsedTimerId) {
				window.clearInterval(this.elapsedTimerId)
				this.elapsedTimerId = null
			}
		},

		onVerificationTimeout() {
			this.step = STEPS.TIMEOUT
			this.stopVerificationStatusPolling()
			this.stopElapsedTimer()
		},

		stopVerificationStatusPolling() {
			if (this.verificationStatusIntervalId) {
				clearInterval(this.verificationStatusIntervalId)
				this.verificationStatusIntervalId = null
			}
		},

		getVerificationStatusFromResponse(response) {
			const partnerData = response?.get?.('data', {}) || {}

			return (
				partnerData.ob_caller_id_verification_status ||
				this.partner?.ob_caller_id_verification_status ||
				''
			)
		},

		async pollVerificationStatus(partnerId) {
			if (this.step !== STEPS.CALLING) {
				return
			}

			const response = await this.getPartner(partnerId)
			const status = String(
				this.getVerificationStatusFromResponse(response)
			)
				.trim()
				.toLowerCase()

			if (status === 'verified') {
				this.stopVerificationStatusPolling()
				this.onVerificationSuccess()
			}
		},

		async checkVerificationStatus() {
			const partnerId = parseInt(this.$route.params.partner, 10)
			if (!partnerId) {
				return
			}

			this.stopVerificationStatusPolling()
			await this.pollVerificationStatus(partnerId)

			this.verificationStatusIntervalId = window.setInterval(() => {
				this.pollVerificationStatus(partnerId)
			}, 20000)
		},

		async onPlaceVerificationCall() {
			this.isPlacingCall = true
			this.$emit('phone-number-change', this.panelPhoneNumber)

			try {
				const response = await this.verifyOutboundCallerId({
					phoneNumber: this.panelPhoneNumber,
					extensionCode: this.extensionCode,
				})
				if (response.status === 201) {
					this.verificationPin =
						response.response.data.validation_code
					this.step = STEPS.CALLING
					this.startElapsedTimer()
					await this.checkVerificationStatus()
				}
			} catch (error) {
				this.$alert.error(
					error?.response?.message ||
						'Failed to place verification call.'
				)
			} finally {
				this.isPlacingCall = false
			}
		},

		onVerificationSuccess() {
			this.stopVerificationStatusPolling()
			this.stopElapsedTimer()
			this.step = STEPS.SUCCESS
			this.$emit('verified', this.panelPhoneNumber)
		},

		...mapActions({
			getPartner: 'partners/find',
			verifyOutboundCallerId: 'auth/verifyOutboundCallerId',
		}),
	},

	mixins: [PanelMixin],

	name: 'VerifyCallerIdPanel',

	props: {
		phoneNumber: {
			type: String,
			default: '',
		},
	},

	watch: {
		phoneNumber: {
			immediate: true,
			handler(value) {
				if (this.step === STEPS.SETUP) {
					this.panelPhoneNumber = this.getSanitizedPhoneNumber(value)
				}
			},
		},

		show(visible) {
			if (visible) {
				this.resetForm()
			} else {
				this.stopVerificationStatusPolling()
				this.stopElapsedTimer()
			}
		},
	},

	beforeDestroy() {
		this.stopElapsedTimer()
		this.stopVerificationStatusPolling()
	},

	data() {
		return {
			STEPS,
			step: STEPS.SETUP,
			panelPhoneNumber: '',
			extensionCode: '',
			panelPhoneErrorMessage: '',
			verificationPin: '',
			elapsedSeconds: 0,
			elapsedTimerId: null,
			verificationStatusIntervalId: null,
			isPlacingCall: false,
		}
	},
}
</script>
